import { useCallback } from 'react';

import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PPA_PACK_ARCH_LEGEND_OR_ROW_SELECTED } from 'utils/mixpanelEventNames';
import type { TableRow } from './packArchitectureTable.helpers';

type UsePackArchitectureTableRowInteractionsParams = {
    activeSegmentKey: string | null;
    selectSegment: (segmentKey: string) => void;
    clearSelection: () => void;
    submitTableSearch: (override?: string) => void;
};

export function usePackArchitectureTableRowInteractions({
    activeSegmentKey,
    selectSegment,
    clearSelection,
    submitTableSearch,
}: UsePackArchitectureTableRowInteractionsParams) {
    const { bubbleRepresents, xAxis, yAxis } = usePackArchitectureContext();

    const getRowClassName = useCallback(
        (record: TableRow) => {
            if (!activeSegmentKey) return 'table-row-clickable';
            if (record.key === activeSegmentKey) return 'table-row-selected table-row-clickable';
            return 'table-row-dimmed table-row-clickable';
        },
        [activeSegmentKey],
    );

    const handleRowClick = useCallback(
        (record: TableRow) => {
            trackEventMixpanel(PPA_PACK_ARCH_LEGEND_OR_ROW_SELECTED, {
                selection_source: 'table',
                bubble_represents: bubbleRepresents,
                bubble_represents_value: record.label,
                x_axis: xAxis,
                y_axis: yAxis,
            });
            selectSegment(record.key);
        },
        [bubbleRepresents, selectSegment, xAxis, yAxis],
    );

    const clearHighlight = useCallback(() => {
        clearSelection();
    }, [clearSelection]);

    const handleSubmitSearch = useCallback(
        (override?: string) => {
            submitTableSearch(override);
        },
        [submitTableSearch],
    );

    return {
        getRowClassName,
        handleRowClick,
        clearHighlight,
        handleSubmitSearch,
    };
}
